import { useBrandGroupsListQuery } from "@saleor/brands/queries";
import {
  brandsListUrl,
  brandsUrl,
  BrandsUrlQueryParams
} from "@saleor/brands/url";
import {
  BrandGroupListFilterOpts,
  BrandGroupsListFilterKeys,
  createFilterStructure,
  getFilterOpts,
  getFilterVariables
} from "@saleor/brands/views/BrandsListPage/filters";
import FilterBar from "@saleor/components/FilterBar";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { FilterPageProps, ListViews, TabPageProps } from "@saleor/types";
import { BrandGroupSortField, OrderDirection } from "@saleor/types/globalTypes";
import React from "react";
import { useIntl } from "react-intl";

import BrandsList from "../BrandsList";

export interface BrandGroupsListPageProps
  extends TabPageProps,
    FilterPageProps<BrandGroupsListFilterKeys, BrandGroupListFilterOpts> {
  params: BrandsUrlQueryParams;
}

const BrandGroupsListPage: React.FC<BrandGroupsListPageProps> = ({
  filterOpts,
  initialSearch,
  onAll,
  onFilterChange,
  onSearchChange,
  onTabChange,
  onTabDelete,
  onTabSave,
  currencySymbol,
  currentTab,
  params,
  tabs,
  ...listProps
}) => {
  const intl = useIntl();
  const paginate = usePaginator();
  const navigate = useNavigator();

  const { settings, updateListSettings } = useListSettings(
    ListViews.BRANDGROUPS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);
  const sortBy = {
    direction: OrderDirection.ASC,
    field: BrandGroupSortField.NAME
  };

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sortBy
    }),
    [params]
  );

  const { data, loading } = useBrandGroupsListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.brandGroups.pageInfo),
    paginationState,
    params
  );

  React.useEffect(
    () =>
      navigate(
        brandsListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const structure = createFilterStructure(intl, getFilterOpts(params));

  return (
    <>
      <FilterBar
        allTabLabel={intl.formatMessage({
          defaultMessage: "All",
          description: "tab name"
        })}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterStructure={structure}
        initialSearch={initialSearch}
        searchPlaceholder={intl.formatMessage({
          defaultMessage: "Search Brand"
        })}
        tabs={tabs}
        onAll={onAll}
        onFilterChange={onFilterChange}
        onSearchChange={onSearchChange}
        onTabChange={onTabChange}
        onTabDelete={onTabDelete}
        onTabSave={onTabSave}
      />
      <BrandsList
        settings={settings}
        onRowClick={id => () => navigate(brandsUrl(id))}
        toolbar={""}
        toggle={function(id: string): void {
          throw new Error(id ? "Function not implemented." : "implemented");
        }}
        toggleAll={function(
          items: React.ReactNodeArray,
          selected: number
        ): void {
          throw new Error(
            items || selected ? "Function not implemented." : "implemented"
          );
        }}
        isChecked={function(id: string): boolean {
          throw new Error(id ? "Function not implemented." : "implemented");
        }}
        selected={0}
        brandGroups={maybe(() =>
          data.brandGroups.edges.map(edges => edges.node)
        )}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onUpdateListSettings={updateListSettings}
        {...listProps}
      />
    </>
  );
};

export default BrandGroupsListPage;
